import React, { useState, useEffect, useMemo } from "react";
import {
    Button,
    Rate,
    Typography,
    message,
    Select,
    Tabs,
    Avatar,
    Modal,
    Spin,
    Divider,
} from "antd";
import {
    SearchOutlined,
    ReloadOutlined,
    StarFilled,
    MessageOutlined,
    UserOutlined,
    StarOutlined,
    ShoppingOutlined,
    ShopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ProductFeedbackServ from "../../service/ProductFeedbackServ";
import Breadcrumbs from "../../component/Breadcrumbs";
import EmptyState from "../../util/EmptyState";
import DataTable from "../../component/ui/DataTable";
import TableImage from "../../component/ui/TableImage";
import QuickFilter from "../../component/ui/QuickFilter";
import { getImageUrl as getNormalizedImageUrl } from "../../component/SafeImage";

const { Title } = Typography;

const SellerProductFeedback = () => {
    const [searchText, setSearchText] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const [productFeedback, setProductFeedback] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalProductFeedback, setTotalProductFeedback] = useState(0);
    const [categories, setCategories] = useState([]);
    const [quickFilter, setQuickFilter] = useState(undefined);
    const [dateRange, setDateRange] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedRating, setSelectedRating] = useState(null);
    const [shops, setShops] = useState([]);
    const [selectedShop, setSelectedShop] = useState(null);
    const [activeTab, setActiveTab] = useState("All");
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    const stats = useMemo(() => {
        if (!productFeedback.length && !totalProductFeedback) return { avgRating: 0, totalReviews: 0 };
        const avg = productFeedback.reduce((acc, curr) => acc + curr.rating, 0) / (productFeedback.length || 1);
        return { avgRating: avg.toFixed(1), totalReviews: totalProductFeedback };
    }, [productFeedback, totalProductFeedback]);

    const fetchProductFeedback = async () => {
        setLoading(true);
        try {
            const filterObj = {};
            if (selectedShop) filterObj.shopId = selectedShop;
            if (selectedCategory) filterObj["product.categoryId"] = selectedCategory;
            if (selectedRating) filterObj.rating = selectedRating;
            if (dateRange && dateRange[0] && dateRange[1]) {
                filterObj.createdAt = {
                    $gte: dateRange[0].startOf('day').toISOString(),
                    $lte: dateRange[1].endOf('day').toISOString(),
                };
            }

            const params = {
                limit: pageSize,
                skip: (page - 1) * pageSize,
                filter: JSON.stringify(filterObj),
            };
            const response = await ProductFeedbackServ.index(params);
            setProductFeedback(response?.data || []);
            setTotalProductFeedback(response?.total || 0);
        } catch (error) {
            console.error(error);
            message.error("Failed to fetch product feedback");
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const CategoryServ = (await import("../../service/CategoryServ")).default;
            const res = await CategoryServ.index({ limit: "all" });
            const allCats = res.data || [];
            setCategories(allCats.filter(cat => !cat.parentId || cat.parentId === null || cat.parentId === ""));
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    };

    const fetchShops = async () => {
        try {
            const ShopServ = (await import("../../service/ShopServ")).default;
            const res = await ShopServ.getSellerShops();
            setShops(res.data || []);
        } catch (err) {
            console.error("Error fetching shops:", err);
        }
    };

    useEffect(() => {
        fetchCategories();
        fetchShops();
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchProductFeedback();
        }, 500);
        return () => clearTimeout(handler);
    }, [pageSize, page, selectedCategory, selectedRating, selectedShop, dateRange]);

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchText(value);
        const filtered = productFeedback.filter(
            (item) =>
                item.product?.name?.toLowerCase().includes(value) ||
                `${item.user?.firstName} ${item.user?.lastName}`.toLowerCase().includes(value) ||
                item.comment?.toLowerCase().includes(value)
        );
        setFilteredData(filtered);
    };

    const handlePageChange = (pg) => {
        setPage(pg);
        const pageContent = document.querySelector('.page-content');
        if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
        else window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleReset = () => {
        setSearchText("");
        setFilteredData([]);
        setSelectedCategory(null);
        setSelectedRating(null);
        setSelectedShop(null);
        setDateRange(null);
        setQuickFilter(undefined);
        setPage(1);
        setActiveTab("All");
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        setSelectedRating(key === "All" ? null : parseInt(key));
        setPage(1);
    };

    const statusTabs = [
        { key: "All", label: "All Feedback" },
        { key: "5", label: "5 Stars" },
        { key: "4", label: "4 Stars" },
        { key: "3", label: "3 Stars" },
        { key: "2", label: "2 Stars" },
        { key: "1", label: "1 Star" },
    ];

    const columns = [
        {
            title: "Image",
            dataIndex: "product",
            key: "productImage",
            width: 70,
            align: "center",
            render: (product) => <TableImage src={getNormalizedImageUrl(product?.imageUrl)} alt="product" size={50} />,
        },
        {
            title: "Product Name",
            dataIndex: "product",
            key: "product",
            render: (product) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '13px' }}>{product?.name || "—"}</span>
                </div>
            ),
        },
        {
            title: "Customer",
            key: "customer",
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar
                        size="small"
                        src={record.user?.imageUrl ? getNormalizedImageUrl(record.user?.imageUrl) : null}
                        icon={<UserOutlined style={{ color: '#64748b' }} />}
                        style={{ background: '#f1f5f9', border: '1px solid #e2e8f0' }}
                    />
                    <span style={{ fontWeight: 600, color: '#475569', fontSize: '12px' }}>
                        {record.user ? `${record.user.firstName} ${record.user.lastName}` : record.userName || "—"}
                    </span>
                </div>
            ),
        },
        {
            title: "Rating",
            dataIndex: "rating",
            key: "rating",
            render: (rating) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <StarFilled style={{ color: '#faad14', fontSize: 13 }} />
                    <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '13px' }}>{rating}</span>
                </span>
            ),
        },
        {
            title: "Date",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => (
                <span style={{ color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                    {new Date(date).toLocaleDateString()}
                </span>
            ),
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={async (e) => {
                        e.stopPropagation();
                        setDetailsVisible(true);
                        setSelectedRecord(record);
                        setModalLoading(true);
                        try {
                            const fullRes = await ProductFeedbackServ.single(record._id || record.id);
                            setSelectedRecord(fullRes?.data || fullRes);
                        } catch (err) {
                            console.error("Error fetching full feedback details:", err);
                        } finally {
                            setModalLoading(false);
                        }
                    }}
                    style={{
                        borderRadius: 3,
                        fontWeight: 600,
                        background: '#ffffffff',
                        color: '#004754ff',
                        border: '1px solid #e4e4e7',
                        boxShadow: '0 0 0 1px #e4e4e7',
                        height: 'auto',
                        padding: '0px 8px',
                        fontSize: 13,
                    }}
                >
                    View
                </Button>
            ),
        },
    ];

    return (
        <>
            <div className="pcu-padding-24">
                <div className="pcu-margin-bottom-16">
                    <Breadcrumbs />
                </div>

                <div className="sd-container" style={{ padding: '24px' }}>
                    {/* Premium Header */}
                    <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                <MessageOutlined style={{ fontSize: '22px', color: '#4776E6' }} />
                            </div>
                            <div>
                                <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>Product Feedback</h1>
                                <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>
                                    {totalProductFeedback} feedback entries
                                    {stats.avgRating > 0 && (
                                        <span style={{ marginLeft: 12, color: '#faad14', fontWeight: 600 }}>
                                            <StarFilled style={{ marginRight: 4 }} />{stats.avgRating} avg rating
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Star Tabs */}
                    <div style={{ marginBottom: '24px', borderBottom: '1px solid #f1f5f9' }}>
                        <Tabs
                            activeKey={activeTab}
                            onChange={handleTabChange}
                            items={statusTabs}
                            className="premium-tabs-minimal"
                            style={{ marginBottom: -1 }}
                        />
                    </div>

                    {/* Filter Bar */}
                    <div className="pcu-padding-0">
                        <div className="ss-filter-bar" style={{ background: 'transparent', border: 'none', padding: '0 0 16px 0', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', alignItems: 'flex-end' }}>

                            {/* Search */}
                            <div className="ss-filter-field">
                                <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>SEARCH FEEDBACK</span>
                                <div className="ss-search-wrap">
                                    <SearchOutlined className="ss-search-icon" style={{ color: '#94a3b8' }} />
                                    <input
                                        className="ss-search-input"
                                        placeholder="Search feedback..."
                                        value={searchText}
                                        onChange={handleSearch}
                                        style={{ height: '38px', borderRadius: '10px', fontSize: '13px' }}
                                    />
                                </div>
                            </div>

                            {/* Shop */}
                            <div className="ss-filter-field">
                                <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>FILTER BY SHOP</span>
                                <Select
                                    placeholder="All Shops"
                                    style={{ width: '100%', height: '38px' }}
                                    allowClear
                                    onChange={setSelectedShop}
                                    value={selectedShop}
                                    className="pcu-radius-10"
                                >
                                    <Select.Option value={null}>All Shops</Select.Option>
                                    {shops.map(shop => <Select.Option key={shop._id} value={shop._id}>{shop.name}</Select.Option>)}
                                </Select>
                            </div>

                            {/* Category */}
                            <div className="ss-filter-field">
                                <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>FILTER BY CATEGORY</span>
                                <Select
                                    placeholder="All Categories"
                                    style={{ width: '100%', height: '38px' }}
                                    allowClear
                                    onChange={setSelectedCategory}
                                    value={selectedCategory}
                                    className="pcu-radius-10"
                                >
                                    <Select.Option value={null}>All Categories</Select.Option>
                                    {categories.map(cat => <Select.Option key={cat._id} value={cat._id}>{cat.name}</Select.Option>)}
                                </Select>
                            </div>

                            {/* Quick Filter */}
                            <div className="ss-filter-field">
                                <QuickFilter
                                    value={quickFilter}
                                    onChange={(key, range) => {
                                        setQuickFilter(key);
                                        setDateRange(range);
                                    }}
                                    style={{ height: '38px', borderRadius: '10px' }}
                                />
                            </div>

                            {/* Reset */}
                            <div className="ss-filter-reset">
                                <button
                                    className="ss-reset-btn"
                                    onClick={handleReset}
                                    style={{
                                        height: '38px',
                                        width: '100%',
                                        padding: '0 24px',
                                        borderRadius: '10px',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        background: '#fff',
                                        border: '1px solid #e2e8f0',
                                        color: '#64748b',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <ReloadOutlined style={{ fontSize: '14px' }} />
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="pcu-border-top">
                        <DataTable
                            columns={columns}
                            data={searchText ? filteredData : productFeedback}
                            rowKey="id"
                            loading={loading}
                            onRow={() => ({ style: { cursor: 'default' } })}
                            locale={{
                                emptyText: (
                                    <EmptyState
                                        title="No Feedback Found"
                                        description={searchText ? "No feedback matches your search." : "No product feedback collected yet."}
                                        gradient="purple"
                                        icon="inbox"
                                    />
                                )
                            }}
                            pagination={{
                                current: page,
                                pageSize: pageSize,
                                total: totalProductFeedback,
                                onChange: handlePageChange,
                                onShowSizeChange: (_, size) => setPageSize(size),
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Detail Modal */}
            <Modal
                open={detailsVisible}
                onCancel={() => setDetailsVisible(false)}
                footer={null}
                width={580}
                closable={true}
                className="ss-modal"
                centered
                styles={{ body: { padding: 0 } }}
            >
                {modalLoading && !selectedRecord ? (
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                        <Spin size="large" tip="Loading details..." />
                    </div>
                ) : selectedRecord && (
                    <div className="ss-modal-body" style={{ position: 'relative' }}>
                        {modalLoading && (
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.4)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 16 }}>
                                <Spin />
                            </div>
                        )}
                        <div className="ss-modal-hero">
                            <div className="ss-modal-img" style={{ width: 90, height: 90 }}>
                                <img
                                    src={getNormalizedImageUrl(selectedRecord.product?.imageUrl)}
                                    alt={selectedRecord.product?.name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            </div>
                            <div className="ss-modal-info">
                                <h2 className="ss-modal-name">{selectedRecord.product?.name}</h2>
                                <div className="ss-modal-tags">
                                    <span className="ss-tag ss-tag-blue" style={{ background: '#e0f2fe', color: '#0369a1' }}>
                                        <StarOutlined style={{ marginRight: 4 }} /> {selectedRecord.rating} Stars
                                    </span>
                                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                                        {new Date(selectedRecord.createdAt).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="ss-modal-details" style={{ padding: '20px 24px' }}>
                            <div className="ss-modal-row">
                                <div className="ss-modal-label">Customer Name</div>
                                <div className="ss-modal-value" style={{ fontWeight: 700 }}>
                                    {selectedRecord.user ? `${selectedRecord.user.firstName} ${selectedRecord.user.lastName}` : selectedRecord.userName}
                                </div>
                            </div>
                            <div className="ss-modal-row">
                                <div className="ss-modal-label">Rating</div>
                                <div className="ss-modal-value">
                                    <Rate value={selectedRecord.rating} disabled style={{ fontSize: 14 }} />
                                </div>
                            </div>
                            <div className="ss-modal-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                                <div className="ss-modal-label">Comment</div>
                                <div className="ss-modal-value" style={{ textAlign: 'left', width: '100%', background: '#f8fafc', padding: '12px', borderRadius: '8px', color: '#475569', lineHeight: 1.5 }}>
                                    "{selectedRecord.comment || "No comment provided."}"
                                </div>
                            </div>
                        </div>

                        <div className="ss-modal-footer" style={{ padding: '16px 24px', justifyContent: 'flex-end' }}>
                            <button className="ss-modal-close-btn" onClick={() => setDetailsVisible(false)} style={{ height: '40px' }}>
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default SellerProductFeedback;
